import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import LoadingState from '../shared/LoadingState';
import EmptyState from '../shared/EmptyState';
import { calculateAgeFromDateOnly } from '../../utils/dateOnly';
import '../superadmin/DistrictsList.css';

const StudentIntakeList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [successMessage, setSuccessMessage] = useState(null);
  const [importStatuses, setImportStatuses] = useState({}); // { studentId: { hasImportedData: boolean } }
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [parentEditsLocked, setParentEditsLocked] = useState(false);
  const [returningEnrollmentSource, setReturningEnrollmentSource] = useState('csv_import');
  const timeoutRef = useRef(null);

  const parentEditsLockedNote =
    'Form edits are currently locked by the district for this school year. Please contact the district if you need to make changes.';

  // Fetch students only on mount
  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  // Handle success / reconnect messages from URL params (separate effect)
  useEffect(() => {
    const message = searchParams.get('success');
    const studentName = searchParams.get('studentName');
    const reconnected = searchParams.get('reconnected');

    const newParams = new URLSearchParams(searchParams);
    let text = null;

    if (message === 'true' && studentName) {
      text = `Submission complete for ${studentName}`;
      newParams.delete('success');
      newParams.delete('studentName');
    } else if (reconnected === '1') {
      text =
        "Reconnected to your student's existing school record (same last name, date of birth, and last four SSN). You can continue enrollment from here.";
      newParams.delete('reconnected');
    }

    if (!text) {
      return undefined;
    }

    setSuccessMessage(text);
    setSearchParams(newParams, { replace: true });
    timeoutRef.current = setTimeout(() => {
      setSuccessMessage(null);
      timeoutRef.current = null;
    }, 7000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [searchParams, setSearchParams]);

  const fetchStudents = async () => {
    try {
      const [intakeRes, districtRes] = await Promise.all([
        axios.get('/api/student-intake/my-intake'),
        axios.get('/api/districts/current').catch(() => ({ data: null }))
      ]);
      const studentsData = intakeRes.data;
      setStudents(studentsData);
      setParentEditsLocked(!!districtRes?.data?.parent_edits_locked);
      const ret = districtRes?.data?.returning_enrollment_source;
      setReturningEnrollmentSource(
        ret === 'in_app_rollover' || ret === 'csv_import' ? ret : 'in_app_rollover'
      );

      // CSV import match batch (only needed when district uses CSV returning flow)
      if (
        studentsData.length > 0 &&
        user?.role === 'parent' &&
        districtRes?.data?.returning_enrollment_source === 'csv_import'
      ) {
        const ids = studentsData.map(s => s.id).join(',');
        const batchResponse = await axios.get(`/api/returning-student-imports/check-batch?ids=${ids}`).catch(() => ({ data: {} }));
        setImportStatuses(batchResponse.data || {});
      } else {
        setImportStatuses({});
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImportReturningStudents = async () => {
    try {
      setImporting(true);
      setImportResults(null);
      
      const response = await axios.post('/api/returning-student-imports/import');
      
      setImportResults(response.data);
      
      // Refresh import statuses
      await fetchStudents();
    } catch (error) {
      console.error('Import error:', error);
      console.error('Import error response:', error.response?.data);
      setImportResults({
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to import student data'
      });
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/student-intake/my-intake/${id}`);
      setStudents(students.filter(s => s.id !== id));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete student intake:', error);
      alert('Failed to delete student intake record');
    }
  };

  const handleEditStudentInfo = (student) => {
    if (!student?.id) return;
    navigate(`/parent/student-intake/${student.id}/edit`);
  };


  const calculateAge = (dateOfBirth) => {
    // Timezone-safe: treats DOB as a calendar date, not a UTC instant.
    const age = calculateAgeFromDateOnly(dateOfBirth);
    return age == null ? '-' : age;
  };

  if (loading) {
    return <LoadingState message="Loading your students..." fullScreen />;
  }

  return (
    <div className="districts-list">
      <div className="page-header">
        <h2>My Students</h2>
        {/* wraps rather than overflowing: on a phone this row is wider than the screen and
            pushed "Add a New Student" out of sight entirely */}
        <div className="parent-students-toolbar" style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {returningEnrollmentSource === 'csv_import' ? (
            <button
              onClick={handleImportReturningStudents}
              disabled={importing || students.length === 0}
              className="btn-secondary"
              style={{
                opacity: (importing || students.length === 0) ? 0.6 : 1,
                cursor: (importing || students.length === 0) ? 'not-allowed' : 'pointer'
              }}
              title={students.length === 0 ? 'Add students first before checking' : 'Check for previous year registration and prefill available data for returning students'}
            >
              {importing ? 'Checking...' : 'Check for Previous Year Registration'}
            </button>
          ) : null}
          <Link to="/parent/student-intake/new" className="btn-primary">Add a New Student</Link>
        </div>
      </div>

      {importResults && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          backgroundColor: importResults.success ? '#d4edda' : '#f8d7da',
          border: `1px solid ${importResults.success ? '#c3e6cb' : '#f5c6cb'}`,
          borderRadius: '4px',
          color: importResults.success ? '#155724' : '#721c24'
        }}>
          {importResults.success ? (
            <div>
              <strong>Import Results:</strong>
              <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0 }}>
                {importResults.results?.map((result, idx) => (
                  <li key={idx} style={{ marginBottom: '0.25rem' }}>
                    {result.studentName}: {result.status === 'imported' ? '✅ Imported successfully' : 'ℹ️ Already imported'}
                  </li>
                ))}
              </ul>
              {importResults.imported > 0 && (
                <p style={{ margin: '0.5rem 0 0 0', fontWeight: 'bold' }}>
                  {importResults.imported} student(s) imported successfully.
                </p>
              )}
              {importResults.alreadyImported > 0 && (
                <p style={{ margin: '0.5rem 0 0 0' }}>
                  {importResults.alreadyImported} student(s) were already imported.
                </p>
              )}
            </div>
          ) : (
            <div>
              <strong>Import Failed:</strong> {importResults.error || 'Unknown error'}
            </div>
          )}
        </div>
      )}

      {students.length > 0 && returningEnrollmentSource === 'csv_import' && (
        <div style={{
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          background: '#e7f3ff',
          border: '1px solid #b3d9ff',
          borderRadius: '4px',
          color: '#004085',
          fontSize: '0.9rem'
        }}>
          <strong>Note:</strong> The &quot;Check for Previous Year Registration&quot; button will look for existing student records matching your students&apos; last name and last 4 digits of SSN.
          You must add your students first before it can match them. Where a match is found, available data from last year is prefilled for returning students.
        </div>
      )}
      {students.length > 0 && returningEnrollmentSource === 'in_app_rollover' && (
        <div style={{
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          background: 'var(--color-gray-50, #f8f9fa)',
          border: '1px solid var(--color-border, #dee2e6)',
          borderRadius: '4px',
          color: 'var(--color-text-secondary, #495057)',
          fontSize: '0.9rem'
        }}>
          <strong>Returning students:</strong> Students you enrolled in a prior year stay on your list when the district starts a new school year. Use <strong>Returning Student</strong> for their new-year forms (we prefill from last year when we can). Use <strong>Add a New Student</strong> only for children who were not in your account before.
        </div>
      )}

      {successMessage && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          color: '#155724',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{successMessage}</span>
          <button
            onClick={() => {
              // Clear timeout if still active
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
              setSuccessMessage(null);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#155724',
              fontSize: '1.2rem',
              cursor: 'pointer',
              padding: '0 0.5rem'
            }}
          >
            ×
          </button>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Student</h3>
            <p>Are you sure you want to delete this student's information? This action cannot be undone.</p>
            <div className="modal-actions">
              <button onClick={() => handleDelete(deleteConfirm)} className="btn-danger">Delete</button>
              <button onClick={() => setDeleteConfirm(null)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {students.length === 0 ? (
        <EmptyState
          icon="👤"
          title="No Students Yet"
          message="Get started by adding your first student. You'll be able to complete enrollment forms for each student you add."
          action={<Link to="/parent/student-intake/new" className="btn-primary">Add Your First Student</Link>}
        />
      ) : (
        <div className="table-wrapper">
          <table className="data-table parent-students-table">
            <thead>
              <tr>
                <th>Name (Last, First)</th>
                <th>Age</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map(student => (
              <tr key={student.id}>
                  <td>{student?.last_name || ''}, {student?.first_name || ''}</td>
                  <td>{calculateAge(student?.date_of_birth)}</td>
                  <td className="actions student-intake-actions-cell">
                    <div className="actions student-intake-actions-row">
                      <button
                        onClick={() => handleEditStudentInfo(student)}
                        className="btn-edit"
                        style={{ fontSize: '0.85em', padding: '0.4rem 0.8rem' }}
                        title="Edit this student's core intake information"
                      >
                        Edit Student Info
                      </button>
                      {/**
                       * A child part-way through is NOT sent back to the combined packet.
                       *
                       * The packet submits every form it renders and the backend inserts those
                       * rows without deduplication, so a child who owes one form would re-file
                       * the ones they already have. They go to the per-form screen, which adds
                       * only what is missing.
                       *
                       * Counts come from /my-intake (services/parentFormsOutstanding.js) and may
                       * be absent if that lookup failed - hence the explicit > 0 and === false
                       * tests rather than truthiness, so an undefined count falls through to the
                       * old behaviour instead of silently claiming nothing is owed.
                       *
                       * has_core_form gates this deliberately: the packet picks its forms from
                       * the grade typed into it, this count uses the intake's stored grade, and
                       * when those disagree a parent who just finished everything can be told a
                       * form is still missing. Only a child without the New Student Form is
                       * prompted here.
                       */}
                      {student.forms_held_count > 0 && student.forms_owed_count > 0 && student.has_core_form === false ? (
                        <Link
                          to={`/parent/edit-student/${student.id}`}
                          className="btn-edit"
                          style={{
                            fontSize: '0.85em',
                            padding: '0.4rem 0.8rem',
                            textDecoration: 'none',
                            display: 'inline-block',
                            textAlign: 'center',
                            backgroundColor: '#ffc107',
                            borderColor: '#ffc107',
                            color: '#212529'
                          }}
                          title={`This student still needs ${student.forms_owed_count} form${student.forms_owed_count === 1 ? '' : 's'}. Your completed forms are kept - you only fill in what is missing.`}
                        >
                          {`Finish Forms (${student.forms_owed_count} remaining)`}
                        </Link>
                      ) : student.form_status === 'completed' ? (
                        <Link
                          to={`/parent/edit-student/${student.id}`}
                          className="btn-edit"
                          style={{ fontSize: '0.85em', padding: '0.4rem 0.8rem', textDecoration: 'none', display: 'inline-block', textAlign: 'center' }}
                          title="Edit submissions for the current school year"
                        >
                          Edit Submission
                        </Link>
                      ) : (() => {
                        const hasPriorYearSubmission = !!student?.has_prior_year_submission;
                        const currentIncompleteFormType = student?.current_incomplete_form_type;
                        const hasReturningImportData = returningEnrollmentSource === 'in_app_rollover' || importStatuses[student?.id]?.hasImportedData;
                        const shouldUseReturningFlow = currentIncompleteFormType === 'returning_student' || (currentIncompleteFormType !== 'new_student' && hasPriorYearSubmission);
                        const enrollmentLink = shouldUseReturningFlow
                          ? `/parent/returning-student-enrollment/${student.id}`
                          : `/parent/new-student/${student.id}`;
                        const enrollmentLabel = student.form_status === 'partial'
                          ? (shouldUseReturningFlow ? 'Continue Returning Enrollment' : 'Continue New Enrollment')
                          : (shouldUseReturningFlow ? 'Start Returning Enrollment' : 'Start New Enrollment');
                        const enrollmentTitle = student.form_status === 'partial'
                          ? (shouldUseReturningFlow
                            ? 'Continue in-progress returning enrollment forms'
                            : 'Continue in-progress new-student enrollment forms')
                          : (shouldUseReturningFlow
                            ? 'Fill out returning student enrollment forms for this student'
                            : 'Fill out new-student enrollment forms for this student');

                        if (shouldUseReturningFlow && !hasReturningImportData) {
                          return (
                            <button
                              disabled
                              className="btn-secondary"
                              style={{
                                fontSize: '0.85em',
                                padding: '0.4rem 0.8rem',
                                backgroundColor: '#6c757d',
                                borderColor: '#6c757d',
                                opacity: 0.5,
                                cursor: 'not-allowed'
                              }}
                              title="This student should use returning enrollment, but your district requires a previous-year check first. Use Check for Previous Year Registration or contact the school."
                            >
                              Returning Enrollment Locked
                            </button>
                          );
                        }

                        return (
                          <Link
                            to={enrollmentLink}
                            className="btn-edit"
                            style={{
                              fontSize: '0.85em',
                              padding: '0.4rem 0.8rem',
                              textDecoration: 'none',
                              display: 'inline-block',
                              textAlign: 'center',
                              backgroundColor: student.form_status === 'partial' ? '#ffc107' : undefined,
                              borderColor: student.form_status === 'partial' ? '#ffc107' : undefined
                            }}
                            title={enrollmentTitle}
                          >
                            {enrollmentLabel}
                          </Link>
                        );
                      })()}
                      <button
                        onClick={() => setDeleteConfirm(student?.id)}
                        className="btn-delete"
                        style={{ fontSize: '0.85em', padding: '0.4rem 0.8rem' }}
                        title="Delete this student record"
                      >
                        Delete
                      </button>
                    </div>
                    {parentEditsLocked && student.form_status === 'completed' ? (
                      <p className="parent-edits-locked-note">{parentEditsLockedNote}</p>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StudentIntakeList;

